import { Directive, ElementRef, EventEmitter, inject, Input, OnDestroy, Output, Renderer2 } from '@angular/core';

/** Portals the host element to `<body>` as a fixed-position panel that flips above its
 * trigger when there isn't enough room below, so it escapes ancestor overflow/z-index clipping. */
@Directive({
	selector: '[mspDropdownBehaviour]',
	standalone: true,
})
export class DropdownBehaviourDirective implements OnDestroy {
	private readonly panelReference = inject(ElementRef<HTMLElement>);
	private readonly renderer = inject(Renderer2);

	constructor() {
		this.renderer.setStyle(this.panelReference.nativeElement, 'display', 'none');
	}

	@Input('mspDropdownBehaviourTrigger') trigger!: ElementRef<HTMLElement> | HTMLElement;

	// emitted so the consumer closes the dropdown (sets isOpen back to false) on scroll
	@Output('mspDropdownBehaviourCollapsed') collapsed = new EventEmitter<void>();

	@Input('mspDropdownBehaviour')
	set isOpen(value: boolean) {
		this.renderer.setStyle(this.panelReference.nativeElement, 'display', value ? '' : 'none');
		if (value) this.attach();
		else this.detach();
	}

	private originalParent: Node | null = null;
	private originalNextSibling: Node | null = null;
	private readonly repositionHandler = (): void => this.position();
	// ignore scrolls inside the panel itself (e.g. its own option list), only close on outside/page scroll
	private readonly scrollHandler = (event: Event): void => {
		if (this.panelReference.nativeElement.contains(event.target as Node)) return;
		this.collapsed.emit();
	};

	private get triggerElement(): HTMLElement | null {
		if (!this.trigger) return null;
		return this.trigger instanceof ElementRef ? this.trigger.nativeElement : this.trigger;
	}

	private attach(): void {
		const panel = this.panelReference.nativeElement;
		if (!this.triggerElement || this.originalParent) return;

		this.originalParent = panel.parentNode;
		this.originalNextSibling = panel.nextSibling;

		this.renderer.appendChild(document.body, panel);
		this.renderer.setStyle(panel, 'position', 'fixed');
		this.renderer.setStyle(panel, 'margin-top', '0.5rem');
		this.renderer.setStyle(panel, 'z-index', '1000');

		this.position();

		window.addEventListener('scroll', this.scrollHandler, true);
		window.addEventListener('resize', this.repositionHandler);
	}

	private detach(): void {
		const panel = this.panelReference.nativeElement;
		if (!this.originalParent) return;

		window.removeEventListener('scroll', this.scrollHandler, true);
		window.removeEventListener('resize', this.repositionHandler);

		this.renderer.insertBefore(this.originalParent, panel, this.originalNextSibling);
		for (const property of ['position', 'top', 'left', 'width', 'margin-top', 'z-index', 'max-height', 'overflow-y']) {
			this.renderer.removeStyle(panel, property);
		}

		this.originalParent = null;
		this.originalNextSibling = null;
	}

	private position(): void {
		const panel = this.panelReference.nativeElement;
		const triggerElement = this.triggerElement;
		if (!triggerElement) return;

		const triggerRect = triggerElement.getBoundingClientRect();
		const panelHeight = panel.offsetHeight;
		const spaceBelow = window.innerHeight - triggerRect.bottom;
		const openUp = spaceBelow < panelHeight && triggerRect.top > spaceBelow;
		// margin so the panel doesn't touch the viewport edge when its height gets clamped
		const edgeMargin = 8;
		const availableSpace = (openUp ? triggerRect.top : spaceBelow) - edgeMargin;

		this.renderer.setStyle(panel, 'left', `${triggerRect.left}px`);
		this.renderer.setStyle(panel, 'width', `${triggerRect.width}px`);
		this.renderer.setStyle(panel, 'top', openUp ? `${triggerRect.top - panelHeight}px` : `${triggerRect.bottom}px`);
		if (openUp) this.renderer.setStyle(panel, 'margin-top', '-0.5rem');

		if (panelHeight > availableSpace) {
			this.renderer.setStyle(panel, 'top', openUp ? `${edgeMargin}px` : `${triggerRect.bottom}px`);
			this.renderer.setStyle(panel, 'max-height', `${availableSpace}px`);
			this.renderer.setStyle(panel, 'overflow-y', 'auto');
		} else {
			this.renderer.removeStyle(panel, 'max-height');
			this.renderer.removeStyle(panel, 'overflow-y');
		}
	}

	ngOnDestroy(): void {
		this.detach();
	}
}
